import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2, Crown, Shield, User, Eye } from 'lucide-react'
import { toast } from 'sonner'
import { usersApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MemberRole } from '@/types'

interface Member {
  member_id: string
  user_id: string
  member_role: MemberRole
  full_name: string
  user_email: string
  created_at: string
}

interface MemberManagementProps {
  resourceId: string
  resourceType: 'project' | 'board'
  members: Member[]
  isLoading: boolean
  onAddMember: (userId: string, role: MemberRole) => Promise<void>
  onUpdateMember: (userId: string, role: MemberRole) => Promise<void>
  onRemoveMember: (userId: string) => Promise<void>
  currentUserId?: string
}

const ROLE_CONFIG: Record<MemberRole, { label: string; icon: React.ElementType; color: string }> = {
  OWNER: { label: 'Owner', icon: Crown, color: 'bg-yellow-500' },
  ADMIN: { label: 'Admin', icon: Shield, color: 'bg-blue-500' },
  MEMBER: { label: 'Member', icon: User, color: 'bg-green-500' },
  VIEWER: { label: 'Viewer', icon: Eye, color: 'bg-gray-500' },
}

export default function MemberManagement({
  resourceType,
  members,
  isLoading,
  onAddMember,
  onUpdateMember,
  onRemoveMember,
  currentUserId,
}: MemberManagementProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedRole, setSelectedRole] = useState<MemberRole>('MEMBER')
  const [userSearchOpen, setUserSearchOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch all users for the add member dialog
  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(true),
    enabled: addDialogOpen,
  })

  // Filter out users who are already members
  const memberUserIds = new Set(members.map(m => m.user_id))
  const availableUsers = allUsers.filter(u => !memberUserIds.has(u.user_id))

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user')
      return
    }
    setIsSubmitting(true)
    try {
      await onAddMember(selectedUserId, selectedRole)
      toast.success('Member added successfully')
      setAddDialogOpen(false)
      setSelectedUserId('')
      setSelectedRole('MEMBER')
    } catch (error: any) {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to add member'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateRole = async (userId: string, newRole: MemberRole) => {
    try {
      await onUpdateMember(userId, newRole)
      toast.success('Member role updated')
    } catch (error: any) {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to update member role'
      toast.error(errorMessage)
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    setIsSubmitting(true)
    try {
      await onRemoveMember(memberToRemove.user_id)
      toast.success('Member removed successfully')
      setRemoveDialogOpen(false)
      setMemberToRemove(null)
    } catch (error: any) {
      const err = error.response?.data?.message
      const errorMessage = typeof err === 'string' ? err : error.message || 'Failed to remove member'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const openRemoveDialog = (member: Member) => {
    setMemberToRemove(member)
    setRemoveDialogOpen(true)
  }

  const selectedUser = allUsers.find(u => u.user_id === selectedUserId)

  if (isLoading) {
    return <div className="text-center py-4 text-muted-foreground">Loading members...</div>
  }

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Members ({members.length})</h3>
        <Button size="sm" variant="outline" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Member
        </Button>
      </div>

      {/* Members list */}
      <ScrollArea className="h-[300px]">
        <div className="space-y-2">
          {members.map((member) => {
            const roleConfig = ROLE_CONFIG[member.member_role]
            const RoleIcon = roleConfig.icon
            const isOwner = member.member_role === 'OWNER'
            const isCurrentUser = member.user_id === currentUserId

            return (
              <div
                key={member.member_id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    {member.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-sm">
                      {member.full_name}
                      {isCurrentUser && <span className="text-muted-foreground ml-1">(you)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{member.user_email}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isOwner ? (
                    <Badge variant="secondary" className="gap-1">
                      <RoleIcon className="h-3 w-3" />
                      {roleConfig.label}
                    </Badge>
                  ) : (
                    <Select
                      value={member.member_role}
                      onValueChange={(value: MemberRole) => handleUpdateRole(member.user_id, value)}
                      disabled={isCurrentUser}
                    >
                      <SelectTrigger className="w-[120px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ADMIN">
                          <div className="flex items-center gap-2">
                            <Shield className="h-3 w-3" />
                            Admin
                          </div>
                        </SelectItem>
                        <SelectItem value="MEMBER">
                          <div className="flex items-center gap-2">
                            <User className="h-3 w-3" />
                            Member
                          </div>
                        </SelectItem>
                        <SelectItem value="VIEWER">
                          <div className="flex items-center gap-2">
                            <Eye className="h-3 w-3" />
                            Viewer
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {!isOwner && !isCurrentUser && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => openRemoveDialog(member)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )
          })}

          {members.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No members yet. Add members to collaborate on this {resourceType}.
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
            <DialogDescription>
              Add a user to this {resourceType} and assign their role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* User selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">User</label>
              <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {selectedUser ? (
                      <span>{selectedUser.full_name} ({selectedUser.user_email})</span>
                    ) : (
                      <span className="text-muted-foreground">Select a user...</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandList>
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup>
                        {availableUsers.map((user) => (
                          <CommandItem
                            key={user.user_id}
                            value={`${user.full_name} ${user.user_email}`}
                            onSelect={() => {
                              setSelectedUserId(user.user_id)
                              setUserSearchOpen(false)
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">
                                {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm">{user.full_name}</div>
                                <div className="text-xs text-muted-foreground">{user.user_email}</div>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Role selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Select value={selectedRole} onValueChange={(v: MemberRole) => setSelectedRole(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <div>
                        <div>Admin</div>
                        <div className="text-xs text-muted-foreground">Can manage members and settings</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="MEMBER">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <div>
                        <div>Member</div>
                        <div className="text-xs text-muted-foreground">Can edit content</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="VIEWER">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      <div>
                        <div>Viewer</div>
                        <div className="text-xs text-muted-foreground">Can only view content</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={!selectedUserId || isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {memberToRemove?.full_name} from this {resourceType}?
              They will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveMember} disabled={isSubmitting}>
              {isSubmitting ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

